import * as React from "react"
import {
  BaseAnchor,
  Button,
  Modal,
  ModalCard,
  StyledModal,
  StyledModalBody,
  StyledModalActions,
  StyledModalHeader,
  Spacer,
  Text,
} from "gatsby-interface"
import { NotificationDeploysIcon } from "./NotificationDeploysIcon"
import {
  ui as uiText,
  notificationDeploys as notificationDeploysText,
} from "@modules/locales/default.json"
import {
  NotificationEventType,
  NotificationTransport,
  NotificationIntegration,
  CloudResourceType,
} from "@modules/graphql/types"
import { FormattedMessage } from "@modules/locales"
import { getNotificationDeploysStatus, getTextStyleFromStatus } from "./utils"
import { useRemoveNotificationIntegration } from "./hooks/useRemoveNotificationIntegration"
import { MdArrowForward } from "react-icons/md"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"

type NotificationDeployRowProps = {
  type: NotificationTransport
  status: NotificationEventType
  config?: { url: string }
}

function NotificationDeploysText({
  type,
  status,
  config,
}: NotificationDeployRowProps) {
  if (type === NotificationTransport.OutgoingWebhook) {
    return (
      <FormattedMessage<"url" | "status", "a" | "strong">
        message={notificationDeploysText.messages.triggerHttpPostRequest}
        values={{
          url: config?.url || "",
          status: getNotificationDeploysStatus(status),
        }}
        tags={{
          a: function(url: string) {
            return <BaseAnchor href={url}>{url}</BaseAnchor>
          },
          strong: function(content: string) {
            return <span css={getTextStyleFromStatus(status)}>{content}</span>
          },
        }}
      />
    )
  }

  if (type === NotificationTransport.PullRequestComment) {
    return (
      <FormattedMessage<"status", "strong">
        message={notificationDeploysText.messages.createPullRequestComment}
        values={{
          status: getNotificationDeploysStatus(status),
        }}
        tags={{
          strong: function(content: string) {
            return <span css={getTextStyleFromStatus(status)}>{content}</span>
          },
        }}
      />
    )
  }

  if (type === NotificationTransport.SlackWebhook) {
    return (
      <FormattedMessage<"status", "strong">
        message={notificationDeploysText.messages.slackWebhook}
        values={{
          status: getNotificationDeploysStatus(status),
        }}
        tags={{
          strong: function(content: string) {
            return <span css={getTextStyleFromStatus(status)}>{content}</span>
          },
        }}
      />
    )
  }

  return null
}

export type NotificationIntegrationRowProps = {
  notificationIntegration: NotificationIntegration
  resourceId: string
  resourceType: CloudResourceType
}

export function NotificationIntegrationRow({
  notificationIntegration,
  resourceId,
  resourceType,
}: NotificationIntegrationRowProps) {
  const [
    removeNotificationIntegration,
    { loading, error },
  ] = useRemoveNotificationIntegration({
    resourceId,
    resourceType,
  })

  const [isOpenedModal, setIsOpenedModal] = React.useState(false)
  const handleClose = () => setIsOpenedModal(false)

  return (
    <React.Fragment>
      <div css={{ display: "flex" }}>
        <span aria-hidden={true}>
          <NotificationDeploysIcon type={notificationIntegration.transport} />
        </span>

        <Spacer size={3} direction="horizontal" />

        <Text size="S" css={{ flex: 1, wordWrap: "break-word" }}>
          <NotificationDeploysText
            type={notificationIntegration.transport}
            status={notificationIntegration.event}
            config={notificationIntegration.config}
          />
        </Text>

        <Spacer size={5} direction="horizontal" />

        <div>
          <Button
            variant="SECONDARY"
            tone="DANGER"
            size="S"
            onClick={() => setIsOpenedModal(true)}
          >
            {uiText.actions.remove}
          </Button>
        </div>
      </div>

      {error && <ErrorAlert>{error.message}</ErrorAlert>}

      <Modal
        aria-label={notificationDeploysText.labels.addWebhookDeployNotification}
        onDismiss={handleClose}
        isOpen={isOpenedModal}
        type="error"
      >
        <ModalCard>
          <StyledModal variant="WARNING">
            <StyledModalHeader onCloseButtonClick={handleClose}>
              {notificationDeploysText.headers.removeNotificationIntegration}
            </StyledModalHeader>

            <StyledModalBody>
              {
                notificationDeploysText.messages
                  .removeNotificationIntegrationContent
              }

              <StyledModalActions>
                <Button
                  onClick={handleClose}
                  disabled={loading}
                  variant="SECONDARY"
                  tone="DANGER"
                >
                  {uiText.actions.cancel}
                </Button>

                <Button
                  rightIcon={<MdArrowForward />}
                  tone="DANGER"
                  onClick={() =>
                    removeNotificationIntegration(
                      notificationIntegration.id
                    ).then(handleClose)
                  }
                  loading={loading}
                >
                  {
                    notificationDeploysText.actions
                      .yesRemoveNotificationIntegration
                  }
                </Button>
              </StyledModalActions>
            </StyledModalBody>
          </StyledModal>
        </ModalCard>
      </Modal>
    </React.Fragment>
  )
}
